import { useAppSelector } from "@app/hooks"
import { Topic } from "@masterschool/course-builder-api"
import { Box } from "@mui/material"
import { MultiLanguageTitleTextField } from "../../components/multiLanguageFields/multiLanguageTitleTextField"
import MultiLanguageUrlTextField from "../../components/multiLanguageFields/multipleLanguageUrlTextField"
import TimeEstimationTextField from "../../components/timeEstimationTextField"
import { useMaestroLesson } from "../custom-hooks/useEditElementItem"
import { useSelectedMaestroLesson } from "../custom-hooks/useSelectedElementItem"
import EditLessonType from "./EditLessonType"
import { selectPublishValidations } from "@features/courseEditor/courseValidationsSelectors"
import EditorTextField from "../../components/editorTextField"

function MaestroLessonEditor(props: { topic: Topic }) {
  const { topic } = props
  const editingLanguage = useAppSelector(
    (state) => state.courseEditor.editingLanguage,
  )
  const maestroLesson = useSelectedMaestroLesson()
  const editMaestroLesson = useMaestroLesson()
  const validations = useAppSelector(selectPublishValidations)
  if (!maestroLesson) {
    return undefined
  }
  const maestroLessonValidations = validations?.[maestroLesson.id] ?? undefined
  const isMissingTrackId =
    maestroLessonValidations?.includes("MISSING_MAESTRO_TRACK_ID") ?? false
  const isInvalidTrackId =
    maestroLessonValidations?.includes("INVALID_MAESTRO_TRACK_ID") ?? false
  const isInvalidVersion =
    maestroLessonValidations?.includes("INVALID_MAESTRO_VERSION") ?? false

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        gap: "24px",
        padding: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            gap: "16px",
          }}
        >
          <MultiLanguageTitleTextField
            elementId={maestroLesson.id}
            language={editingLanguage}
            defaultLanguageValue={maestroLesson.title}
            extraLanguageValueRecord={maestroLesson.extraLanguageTitleRecord}
            debounce
            onChangeDefaultLanguage={(value) => {
              editMaestroLesson(maestroLesson, "title", value, topic)
            }}
            onChangeExtraLanguage={(value) => {
              editMaestroLesson(
                maestroLesson,
                "extraLanguageTitleRecord",
                value,
                topic,
              )
            }}
          />
          <EditLessonType topic={topic} />
        </Box>
        <TimeEstimationTextField
          elementId={maestroLesson.id}
          value={maestroLesson.estimatedDuration}
          debounce
          onChange={(e) => {
            editMaestroLesson(
              maestroLesson,
              "estimatedDuration",
              parseInt(e.target.value),
              topic,
            )
          }}
        />
        <MultiLanguageUrlTextField
          elementId={maestroLesson.id}
          language={editingLanguage}
          defaultLanguageValue={maestroLesson.trackId}
          extraLanguageValueRecord={maestroLesson.extraLanguageUrlRecord}
          debounce
          label="Track ID"
          onChangeDefaultLanguage={(newValue) => {
            editMaestroLesson(maestroLesson, "trackId", newValue, topic)
          }}
          onChangeExtraLanguage={(newExtraLanguageValueRecord) => {
            editMaestroLesson(
              maestroLesson,
              "extraLanguageUrlRecord",
              newExtraLanguageValueRecord,
              topic,
            )
          }}
          error={isMissingTrackId}
          helperText={
            isMissingTrackId || isInvalidTrackId
              ? "Please provide a valid Track ID, make sure id does not contain { } brackets"
              : undefined
          }
          required
        />
        <EditorTextField
          label="Version (optional)"
          type="number"
          value={maestroLesson.version}
          debounce
          onChange={(e) => {
            const newValue = parseInt(e.target.value)
            if (isNaN(newValue)) {
              return
            }
            editMaestroLesson(maestroLesson, "version", newValue, topic)
          }}
          error={isInvalidVersion}
          helperText={
            isInvalidVersion
              ? "Please provide a valid version number"
              : undefined
          }
        />
      </Box>
    </Box>
  )
}

export default MaestroLessonEditor
